import { Skeleton } from 'antd'
import React from 'react'
import styled, { keyframes } from 'styled-components'

import {
  cardBorder,
  darkBlueFontColor,
  pageBackgroundGrey,
} from 'assets/styles/variables'
import { chartFactory } from 'features/charts-v2/chart-factory'
import type { TimeBucketOption } from 'features/charts-v2/types'
import type { ChartProps } from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import { TREND_DETECTION_PRIMARY_BLUE } from 'features/trend-detection/constants'
import type { Chart, ChartIdentifier } from 'features/trend-detection/types'
import { formatDateLabel } from 'features/trend-detection/utils/helpers'

const ValidationErrorBox = styled.div`
  padding: 20px;
  color: #666;
`

const LineChartContainer = styled.div<{
  $height?: string | number
  $minHeight?: string
}>`
  width: 100%;
  height: ${(p) => (p.$height != null ? String(p.$height) : '100%')};
  min-height: ${(p) => p.$minHeight ?? '200px'};
  position: relative;
  margin-bottom: 24px;
  overflow: visible;

  .recharts-line-dots circle {
    fill: #3b82f6 !important;
    stroke: #1e40af !important;
    stroke-width: 2 !important;
    r: 5 !important;
  }

  .recharts-line-dots circle:hover {
    r: 6 !important;
    fill: #1e40af !important;
  }
`

const ChartWrapper = styled.div<{ $height?: string | number }>`
  width: 100%;
  height: ${(p) => (p.$height != null ? String(p.$height) : 'auto')};
  min-height: 200px;
  position: relative;
  margin-bottom: 8px;
  overflow: visible;
`

/** Infer time bucket from chart identifier domains (e.g. dt_bucket_day, dt_bucket_week). */
function getTimeBucketOptionFromDomains(
  identifier: ChartIdentifier,
): TimeBucketOption {
  const first = identifier.domains[0]
  if (
    first &&
    typeof first === 'object' &&
    'fn' in first &&
    typeof (first as { fn: string }).fn === 'string'
  ) {
    switch ((first as { fn: string }).fn) {
      case 'dt_bucket_day':
        return 'day'
      case 'dt_bucket_month':
        return 'month'
      case 'dt_bucket_week':
      default:
        return 'week'
    }
  }
  return 'week'
}

export const SimpleChart = React.memo<{
  chart: Chart
  chartType?: ChartTypeV2
  height?: string | number
}>(function SimpleChart({
  chart,
  chartType = ChartTypeV2.BAR_HORIZONTAL,
  height = '400px',
}) {
  const strategy = chartFactory.getStrategy(chartType)

  if (!strategy) {
    return <div>Unsupported chart type</div>
  }

  const hasTimeDomain = chartType === ChartTypeV2.LINE

  let chartData = chart
  if (hasTimeDomain && chart?.data) {
    const rawData = chart.data
    chartData = {
      ...chart,
      data: rawData.map((item) => ({
        ...item,
        category: item.category.map(formatDateLabel),
      })),
    }
  }

  const chartProps: ChartProps = {
    data: chartData as unknown as ChartProps['data'],
    selectedMetrics: ['record_count'],
    // Bar/Pie strategies use selectedBreakdowns.length to choose transform: 0 → single "Total";
    // >0 → per-category (name from category[0]). Issue breakdown charts have one category per row.
    selectedBreakdowns:
      chartType === ChartTypeV2.BAR_HORIZONTAL || chartType === ChartTypeV2.PIE
        ? [{ kind: 'standard', id: 'brand', grouped: false }]
        : undefined,
    timeBucket: hasTimeDomain
      ? getTimeBucketOptionFromDomains(chart.identifier)
      : null,
  }

  const transformedData = strategy.transform(chartProps)
  const validation = strategy.validate(chartProps)

  if (!validation.isValid) {
    return (
      <ValidationErrorBox>
        {validation.errorTitle}
        {validation.errorDescription ? ` — ${validation.errorDescription}` : ''}
      </ValidationErrorBox>
    )
  }

  const renderProps = {
    ...chartProps,
    transformedData,
    showDatapointLabels: chartType !== ChartTypeV2.LINE,
  }

  const renderedChart = strategy.render(renderProps)

  if (chartType === ChartTypeV2.LINE) {
    return (
      <LineChartContainer $height={height} $minHeight="200px">
        {renderedChart}
      </LineChartContainer>
    )
  }

  return <ChartWrapper $height={height}>{renderedChart}</ChartWrapper>
})

const skeletonShimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const SkeletonBox = styled.div<{ width?: string; height?: string }>`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${skeletonShimmer} 1.5s infinite;
  border-radius: 4px;
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '16px'};
`

const BAR_HEIGHT_PERCENTS = [55, 75, 35, 60, 45, 80, 30, 65, 50, 70, 40, 58]
const SKELETON_CHART_HEIGHT = 200

const SkeletonBarsContainer = styled.div`
  flex: 1;
  height: ${SKELETON_CHART_HEIGHT}px;
  min-height: ${SKELETON_CHART_HEIGHT}px;
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  padding: 8px 0 24px 0;
  gap: 8px;
`

const SkeletonBarItem = styled(SkeletonBox)`
  min-width: 24px;
  max-width: 56px;
  flex: 1;
`

export function ChartBarSkeleton(): JSX.Element {
  return (
    <SkeletonBarsContainer>
      {BAR_HEIGHT_PERCENTS.map((pct, i) => (
        <SkeletonBarItem
          key={i}
          width="100%"
          height={`${(pct / 100) * SKELETON_CHART_HEIGHT}px`}
        />
      ))}
    </SkeletonBarsContainer>
  )
}

const VerbatimSkeletonCard = styled.div`
  padding: 12px;
  background-color: ${pageBackgroundGrey};
  border-radius: 4px;
  border-left: 3px solid ${TREND_DETECTION_PRIMARY_BLUE};
`

const PostsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const VERBATIMS_SKELETON_COUNT = 4

export function VerbatimsSkeleton(): JSX.Element {
  return (
    <PostsList>
      {Array.from({ length: VERBATIMS_SKELETON_COUNT }).map((_, i) => (
        <VerbatimSkeletonCard key={i}>
          <Skeleton
            active
            avatar={false}
            paragraph={{ rows: 2 }}
            title={false}
          />
        </VerbatimSkeletonCard>
      ))}
    </PostsList>
  )
}

export const NoChartData = styled.p`
  font-size: 13px;
  color: ${darkBlueFontColor};
  opacity: 0.5;
  font-style: italic;
  text-align: center;
  padding: 40px 0;
  margin: 0;
`

export const ChartLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  padding: 40px 0;
  min-height: 260px;
`

export const ChartSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid ${cardBorder};
`

export const ChartContainer = styled.div`
  width: 100%;
  height: 280px;
  position: relative;
`

export const ProductChartContainer = styled(ChartContainer)`
  height: 280px;
`
